// frontend/src/pages/Home.jsx
import React from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { 
  MessageCircle, 
  TestTube, 
  BarChart3, 
  Zap, 
  Sparkles, 
  Rocket, 
  Shield, 
  Globe,
  Bot,
  TrendingUp,
  ArrowRight,
  Star,
  Play,
  CheckCircle,
  Activity
} from 'lucide-react'
import Button from '../components/common/Button'

const Home = () => {
  const navigate = useNavigate()

  const features = [
    {
      icon: MessageCircle,
      title: 'Conversational Mode',
      description: 'Chat with AI to browse the web interactively and get real-time assistance',
      color: 'text-blue-600',
      gradient: 'from-blue-500 to-cyan-500',
      path: '/conversational',
      stats: '50K+ Conversations'
    },
    {
      icon: TestTube,
      title: 'Automation Mode',
      description: 'Generate and run comprehensive website tests with AI-powered analysis',
      color: 'text-emerald-600',
      gradient: 'from-emerald-500 to-teal-500',
      path: '/automation',
      stats: '10K+ Tests Generated'
    },
    {
      icon: BarChart3,
      title: 'Analytics Dashboard',
      description: 'View detailed performance metrics and insights with beautiful visualizations',
      color: 'text-purple-600',
      gradient: 'from-purple-500 to-pink-500',
      path: '/analytics',
      stats: '99.9% Accuracy'
    }
  ]

  const stats = [
    { label: 'Active Users', value: '10K+', icon: Globe },
    { label: 'Tests Generated', value: '500K+', icon: TestTube },
    { label: 'Success Rate', value: '99.9%', icon: TrendingUp },
    { label: 'Uptime', value: '99.99%', icon: Shield }
  ]

  const testimonials = [
    {
      name: "Sarah Chen",
      role: "QA Engineer",
      company: "TechCorp",
      content: "Test CREW revolutionized our testing workflow. The AI-generated tests are incredibly accurate and save us hours every day.",
      avatar: "👩‍💻"
    },
    {
      name: "Mike Rodriguez",
      role: "DevOps Lead",
      company: "StartupXYZ",
      content: "The conversational mode makes web testing accessible to our entire team. No technical expertise required!",
      avatar: "👨‍💼"
    },
    {
      name: "Emma Thompson",
      role: "Product Manager",
      company: "InnovateLab",
      content: "The analytics dashboard provides insights we never had before. Our product quality has improved dramatically.",
      avatar: "👩‍🔬"
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/30 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-grid-slate-200/20 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))]" />
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-400/20 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-400/20 rounded-full blur-3xl" />
      
      <div className="relative z-10">
        {/* Hero Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            {/* Logo/Brand */}
            <motion.div 
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="flex items-center justify-center mb-8"
            >
              <div className="relative">
                <div className="w-20 h-20 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-2xl flex items-center justify-center shadow-2xl">
                  <Bot className="h-10 w-10 text-white" />
                </div>
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full border-3 border-white animate-pulse"></div>
              </div>
            </motion.div>

            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-6xl md:text-7xl font-bold bg-gradient-to-r from-slate-900 via-blue-900 to-purple-900 bg-clip-text text-transparent mb-6"
            >
              Test CREW
            </motion.h1>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="relative"
            >
              <h2 className="text-2xl md:text-3xl font-semibold text-slate-700 mb-6">
                AI-Powered Testing Platform
              </h2>
              <p className="text-xl text-slate-600 max-w-4xl mx-auto leading-relaxed">
                Revolutionary web testing platform powered by artificial intelligence. 
                Generate comprehensive test cases, run interactive tests, and analyze results 
                with real-time insights and beautiful visualizations.
              </p>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 justify-center mt-10"
            >
              <button
                onClick={() => navigate('/automation')}
                className="group relative px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-2xl font-semibold text-lg shadow-2xl hover:shadow-blue-500/25 transform hover:scale-105 transition-all duration-300"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl blur opacity-60 group-hover:opacity-80 transition-opacity"></div>
                <div className="relative flex items-center space-x-2">
                  <Rocket className="h-6 w-6" />
                  <span>Start Testing Now</span>
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </div>
              </button>
              
              <button
                onClick={() => navigate('/conversational')}
                className="group px-8 py-4 bg-white/80 backdrop-blur-xl text-slate-700 rounded-2xl font-semibold text-lg border border-white/60 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
              >
                <div className="flex items-center space-x-2">
                  <Play className="h-6 w-6" />
                  <span>Watch Demo</span>
                </div>
              </button>
            </motion.div>
          </motion.div>

          {/* Stats Bar */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="bg-white/60 backdrop-blur-2xl rounded-3xl border border-white/60 shadow-2xl p-8 mb-20"
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1 + index * 0.1 }}
                  className="text-center"
                >
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <stat.icon className="h-6 w-6 text-white" />
                  </div>
                  <div className="text-3xl font-bold text-slate-900 mb-1">{stat.value}</div>
                  <div className="text-sm text-slate-600 font-medium">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Features Grid */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2 }}
            className="mb-20"
          >
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-slate-900 mb-4">Powerful Features</h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                Everything you need to test, analyze, and optimize your web applications
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.4 + index * 0.2 }}
                  className="group relative"
                >
                  <div className="absolute inset-0 bg-gradient-to-r opacity-0 group-hover:opacity-10 rounded-3xl transition-opacity duration-300" 
                       style={{ background: `linear-gradient(135deg, ${feature.gradient})` }} />
                  
                  <div className="relative bg-white/80 backdrop-blur-2xl rounded-3xl border border-white/60 shadow-xl hover:shadow-2xl p-8 transition-all duration-500 hover:scale-105 cursor-pointer h-full"
                       onClick={() => navigate(feature.path)}>
                    
                    {/* Icon */}
                    <div className={`w-16 h-16 bg-gradient-to-r ${feature.gradient} rounded-2xl flex items-center justify-center mb-6 shadow-lg`}>
                      <feature.icon className="h-8 w-8 text-white" />
                    </div>
                    
                    {/* Content */}
                    <h3 className="text-2xl font-bold text-slate-900 mb-4">{feature.title}</h3>
                    <p className="text-slate-600 mb-6 leading-relaxed">{feature.description}</p>
                    
                    {/* Stats */}
                    <div className="flex items-center justify-between mb-6">
                      <span className="text-sm font-medium text-slate-500">{feature.stats}</span>
                      <div className="flex items-center space-x-1">
                        {[1,2,3,4,5].map((star) => (
                          <Star key={star} className="h-4 w-4 text-yellow-400 fill-current" />
                        ))}
                      </div>
                    </div>
                    
                    {/* CTA */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        navigate(feature.path)
                      }}
                      className={`w-full py-3 px-6 bg-gradient-to-r ${feature.gradient} text-white rounded-xl font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-2`}
                    >
                      <span>Get Started</span>
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Testimonials */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.8 }}
            className="mb-20"
          >
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-slate-900 mb-4">Loved by Teams Worldwide</h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                Join thousands of developers and QA teams who trust Test CREW
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 2 + index * 0.1 }}
                  className="bg-white/80 backdrop-blur-2xl rounded-3xl border border-white/60 shadow-xl p-8"
                >
                  <div className="flex items-center mb-6">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-2xl mr-4">
                      {testimonial.avatar}
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">{testimonial.name}</h4>
                      <p className="text-sm text-slate-600">{testimonial.role} at {testimonial.company}</p>
                    </div>
                  </div>
                  <p className="text-slate-700 leading-relaxed">{testimonial.content}</p>
                  <div className="flex items-center mt-4">
                    {[1,2,3,4,5].map((star) => (
                      <Star key={star} className="h-4 w-4 text-yellow-400 fill-current" />
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Final CTA */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2.4 }}
            className="relative"
          >
            <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-3xl p-1">
              <div className="bg-white/95 backdrop-blur-2xl rounded-3xl p-12 text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Sparkles className="h-8 w-8 text-white" />
                </div>
                
                <h2 className="text-4xl font-bold text-slate-900 mb-4">
                  Ready to Transform Your Testing?
                </h2>
                <p className="text-xl text-slate-600 mb-8 max-w-3xl mx-auto">
                  Join the future of web testing with AI-powered automation, 
                  conversational interfaces, and beautiful analytics.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => navigate('/automation')}
                    className="group px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-2xl font-semibold text-lg shadow-2xl hover:shadow-blue-500/25 transform hover:scale-105 transition-all duration-300"
                  >
                    <div className="flex items-center space-x-2">
                      <TestTube className="h-6 w-6" />
                      <span>Start Automation</span>
                      <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </button>
                  
                  <button
                    onClick={() => navigate('/conversational')}
                    className="group px-8 py-4 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-2xl font-semibold text-lg shadow-2xl hover:shadow-emerald-500/25 transform hover:scale-105 transition-all duration-300"
                  >
                    <div className="flex items-center space-x-2">
                      <MessageCircle className="h-6 w-6" />
                      <span>Start Conversation</span>
                      <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default Home